import { useEffect } from "react";
import {
  PALETTES,
  findClosestPreset,
  AREA_CSS_KEYS,
  type ColorArea,
} from "@/lib/color-palettes";

function getContrastColor(hex: string): string {
  const c = hex.replace("#", "");
  const r = Number.parseInt(c.substring(0, 2), 16);
  const g = Number.parseInt(c.substring(2, 4), 16);
  const b = Number.parseInt(c.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? "#000000" : "#ffffff";
}

const ACCENT_VARS = [
  "--color-accent",
  "--color-accent-foreground",
  "--color-accent-soft",
  "--color-accent-medium",
] as const;

/**
 * Sets accent color CSS variables and palette CSS variables on document root.
 * Accepts undefined or empty string (clears vars on cleanup only).
 * Used by workspace layout (route.tsx) and onboarding wizard preview.
 */
export function useAccentColor(hex: string | undefined) {
  useEffect(() => {
    const root = document.documentElement;
    if (!hex) return;

    root.style.setProperty("--color-accent", hex);
    root.style.setProperty("--color-accent-foreground", getContrastColor(hex));
    root.style.setProperty("--color-accent-soft", `${hex}1a`);
    root.style.setProperty("--color-accent-medium", `${hex}40`);

    // Inject palette CSS variables (6 areas x 8 colors = 48 vars)
    const preset = findClosestPreset(hex);
    const palette = PALETTES[preset];
    const paletteVars: string[] = [];
    for (const [area, colors] of Object.entries(palette)) {
      // Cast needed: Object.entries loses key type
      const cssArea = AREA_CSS_KEYS[area as ColorArea];
      colors.forEach((color: string, i: number) => {
        const varName = `--color-palette-${cssArea}-${i + 1}`;
        root.style.setProperty(varName, color);
        paletteVars.push(varName);
      });
    }

    return () => {
      for (const v of ACCENT_VARS) {
        root.style.removeProperty(v);
      }
      for (const v of paletteVars) {
        root.style.removeProperty(v);
      }
    };
  }, [hex]);
}
